import * as React from 'react';
import { Fragment } from 'react';
import Button from '@atlaskit/button';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import capitalize from 'lodash/capitalize';
import defaultTo from 'lodash/defaultTo';
import flatten from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import negate from 'lodash/negate';
import startCase from 'lodash/startCase';
import toNumber from 'lodash/toNumber';
import sortBy from 'lodash/sortBy';
import set from 'lodash/set';
// @ts-ignore
import PdfDocument24Icon from '@atlaskit/icon-file-type/glyph/pdf-document/24';
import ArrowLeftCircleIcon from '@atlaskit/icon/glyph/arrow-left-circle';
import Lozenge from '@atlaskit/lozenge';
import Spinner from '@atlaskit/spinner';
import ToolTip from '@atlaskit/tooltip';
import { ApolloQueryResult } from '@apollo/client/core/types';
import {
  /*AdaRedirectButton,*/
  analytics,
  createOpenSidebarFromButtonEvent,
} from '@atlassian/temp-analytics';
import { downloadInvoicePdf } from '@atlassian/temp-proxies';
import {
  BillingDoc,
  BillingDocWithLineItems,
  CreditNotetype,
  Invoice,
  InvoiceLineItem,
  InvoiceWithLineItems,
  PaymentMethods,
  TaxItems,
} from '@atlassian/cssxp-types-billing';
import { Organization } from '@atlassian/cssxp-types-organization';
import {
  DOMAIN_PATH,
  INVOICE_PATH_2,
  NEW_DATE_FORMAT,
  NEW_DATE_TIME_FORMAT,
  ORGANIZATION_PATH,
  Permissions,
  Resources,
  SourceSystem,
  RelationshipPathType,
} from '@atlassian/cssxp-typescommon';
import { GloballyAllowedSidebar } from '@atlassian/cssxp-utils-generic';
import {
  isInvoiceCreditable,
  isInvoiceRefundable,
  isPaymentTerm,
  isUncollectible,
} from '@atlassian/cssxp-utils-invoice';
import { isCloudParent, isOrganisationParent, isStride } from '@atlassian/cssxp-utils-product';
import {
  BreadcrumbItem,
  commonTabs,
  FormattedDate,
  isAccess,
  isResourceAccess,
  MoreActionsItems,
  MoreActionsV2,
  OpenSidebarButton,
  RefundedLozenge,
  StatusLozenge,
  SummarySectionV2,
  useFeatureToggle,
  useSidebar,
} from '@css-xp/base';
// @ts-ignore
import AtlassianPartnerIcon from '@css-xp/base/assets/img/icon-atlassian-partner.svg';
import {
  PaymentMethodType,
  InvoicePaymentMethod,
  InvoiceStatus,
  ProductAppLineItem,
  ProductAppLineItemData,
  InvoiceEssotEntitlementItem,
  PaymentOnTerms,
  InvoiceLineItems,
  InvoiceEntitlementItem,
} from '@css-xp/graphtypes/src/generated-types-new';
import { SUBSCRIPTION_CONSTANTS } from '@css-xp/ppc-constants';
import { isCollectionInstanceEntitlement, isCollectionProductEntitlement } from '@css-xp/ppc-utils';
import { generatedTypesNew } from '@css-xp/graphtypes';
import './invoice-page-utils.scss';

interface PdfRedirectProps {
  href: string;
  label?: string;
  transactionAccountId?: string | null | undefined;
  invoiceId?: string;
  children?: React.ReactNode;
  sourceSystem?: string;
}

enum RelationshipType {
  ADDON_DEPENDENCE = 'ADDON_DEPENDENCE',
}

export enum MarginType {
  PARTNER_MARGIN = 'PARTNER_MARGIN',
}
export type AppsGroupData = {
  [key: string]: {
    apps: InvoiceLineItem[];
    groupName?: string;
  };
};
export type CreditItemsLinked = {
  [key: string]: {
    [key: string]: InvoiceLineItem[];
  };
};

export type AddOnsGroupData = {
  [key: string]: {
    addOns: InvoiceLineItem[];
    groupName?: string;
  };
};

export type ProductsGroupContainer = {
  familyContainerId: string;
  defaultFamilyName: string;
  defaultCloudSite: string;
  products: InvoiceLineItem[];
  appsGroup?: AppsGroupData;
  addOnsGroup?: AddOnsGroupData;
  creditItems?: CreditItemsLinked;
  productsItemDetails?: {
    productName: string;
    cloudSiteHostname: string;
  }[];
};

export type ProductsWithFamilyContainer = {
  [key: string]: ProductsGroupContainer;
};

export type ProductWithLinkedApps = {
  [key: string]: InvoiceLineItem[];
};

export type InvoiceLineItemPPC = InvoiceLineItem & {
  currency: string | null;
};

export type InvoiceWithLineItemsPPC = InvoiceWithLineItems & {
  pricingCurrency: string | null;
};

export type ExtendedInvoicePPC = Invoice & {
  pricingCurrency: string | null;
};

const partnerLozenge = (partnerType: string): JSX.Element => (
  <StatusLozenge status={'whitelisted'} label={`${partnerType.toUpperCase()} PARTNER`} />
);

const getInvoiceHeader = (billingDoc: BillingDoc): React.ReactNode => {
  const { invoiceNumber, id, sourceSystem, partnerLevel, pdfLink, transactionAccountId } = billingDoc as Invoice;

  let headerValue;
  if (sourceSystem === SourceSystem.CCP) {
    headerValue = <>{invoiceNumber ? invoiceNumber : <>{id}</>}</>;

    return (
      <>
        <div className="invoice-heading">
          {headerValue}
          <div className="view-pdf-btn">
            {sourceSystem === SourceSystem.CCP && pdfLink ? (
              <PdfRedirectButton
                href={pdfLink}
                label="View PDF"
                invoiceId={id}
                transactionAccountId={transactionAccountId}
                sourceSystem={sourceSystem}
              >
                <PdfDocument24Icon label="pdf" />
              </PdfRedirectButton>
            ) : null}
          </div>
          {partnerLevel && partnerLevel.length > 0 && partnerLozenge(partnerLevel)}
        </div>
      </>
    );
  }

  return <>{id}</>;
};

const getInvoiceSubheader = (
  billingDoc: BillingDoc,
  shouldHidePartnerLogo?: boolean,
  deferredPaymentMethod?: PaymentOnTerms[]
): React.ReactNode => {
  const { refunds, dunningStatus, partnerAccountId, paymentMethod, paymentMethodId } = billingDoc as Invoice;
  const isDeferredPaymentMethodSelected = deferredPaymentMethod?.some(
    (paymentMethod) => paymentMethod?.id === paymentMethodId
  );
  let { status } = billingDoc as Invoice;
  const isPartner = !!partnerAccountId;
  const hasPaymentError = isDeferredPaymentMethodSelected ? false : !!dunningStatus?.length;
  const refundMessage = 'One or more credit notes have been issued for this invoice';

  if (paymentMethod === InvoicePaymentMethod.DEFERRED && status === InvoiceStatus.PAYMENT_DEFERRED) {
    status = InvoiceStatus.PAID;
  }

  return (
    <div className="invoice-status">
      {!shouldHidePartnerLogo && !!isPartner && (
        <span data-testid="partner-logo">
          {/*// @ts-ignore */}
          <img src={AtlassianPartnerIcon} height={'15px'} alt="Atlassian partner icon" />
        </span>
      )}
      {status && (
        <Lozenge
          appearance={status === InvoicePaymentMethod.PAID ? 'success' : 'inprogress'}
          testId={`invoice-status-lozenge-${status}`}
        >
          {capitalize(startCase(status ?? ''))}
        </Lozenge>
      )}
      {refunds.length > 0 && (
        <>
          <ToolTip content={refundMessage} tag="span" position="mouse">
            <RefundedLozenge />
          </ToolTip>
        </>
      )}
      {hasPaymentError && <>{commonTabs.isPaymentIssues(status, hasPaymentError)}</>}
    </div>
  );
};

const InvoiceMoreActions = ({
  invoice,
  onComplete,
}: {
  invoice: InvoiceWithLineItems;
  onComplete: () => Promise<ApolloQueryResult<unknown>>;
}) => {
  const { openSidebar, isSidebarOpen } = useSidebar();
  const hasPaymentTerms = invoice.paymentMethod === PaymentMethodType.DEFERRED;
  const [isEnabled] = useFeatureToggle();
  const invoiceActions: MoreActionsItems[] = [
    {
      id: 'create-service-credit',
      content: 'Request credit',
      predicate:
        invoice.sourceSystem === SourceSystem.HAMS &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isAccess(Permissions.INVOICE_CREDIT_NOTE_WRITE),
      isDisabled: invoice?.sourceSystem === 'hams' || !isInvoiceCreditable(invoice),
      action: () => {
        analytics.sendUIEvent(createOpenSidebarFromButtonEvent(GloballyAllowedSidebar.BILLING_SERVICE_CREDIT_FORM));
        openSidebar({
          sidebarType: GloballyAllowedSidebar.BILLING_SERVICE_CREDIT_FORM,
          sidebarOptions: {
            sidebarWidth: '600px',
            isOverlay: true,
          },
          payload: {
            invoiceId: invoice.id,
          },
        });
      },
    },
    {
      id: 'create-refund',
      content: 'Process refund',
      predicate:
        invoice.sourceSystem === SourceSystem.HAMS &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isAccess(Permissions.INVOICE_CREDIT_NOTE_WRITE),
      isDisabled: invoice?.sourceSystem === 'hams' || !isInvoiceRefundable(invoice, hasPaymentTerms),
      action: () => {
        analytics.sendUIEvent(createOpenSidebarFromButtonEvent(GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM));
        openSidebar({
          sidebarType: GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM,
          sidebarOptions: {
            sidebarWidth: '600px',
            isOverlay: true,
          },
          payload: {
            invoiceId: invoice.id,
          },
        });
      },
    },
    {
      id: 'ccp-full-refund',
      content: 'Refund full invoice',
      predicate:
        invoice.sourceSystem === SourceSystem.CCP &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isAccess(Permissions.INVOICE_CREDIT_MEMO_WRITE) &&
        !isUncollectible(invoice),
      isDisabled: !isInvoiceRefundable(invoice, hasPaymentTerms),
      action: () => {
        analytics.sendUIEvent(createOpenSidebarFromButtonEvent(GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM_CCP));
        openSidebar({
          sidebarType: GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM_CCP,
          sidebarOptions: {
            sidebarWidth: '600px',
            isOverlay: true,
          },
          payload: {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            transactionAccountId: invoice.transactionAccountId,
            refundType: CreditNotetype.FULL_ITEM_REFUND,
            lineItems: invoice.lineItems,
            paymentMethod: invoice.paymentMethod,
            invoiceStatus: invoice.status,
            invoiceCreated: invoice.createdTimestamp,
            refetch: onComplete,
          },
        });
      },
    },
    {
      id: 'ccp-tax-refund',
      content: 'Refund tax',
      predicate:
        invoice.sourceSystem === SourceSystem.CCP &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isAccess(Permissions.INVOICE_CREDIT_MEMO_WRITE) &&
        !isUncollectible(invoice),
      isDisabled: !isInvoiceRefundable(invoice, hasPaymentTerms),
      action: () => {
        analytics.sendUIEvent(createOpenSidebarFromButtonEvent(GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM_CCP));
        openSidebar({
          sidebarType: GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM_CCP,
          sidebarOptions: {
            sidebarWidth: '600px',
            isOverlay: true,
          },
          payload: {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            transactionAccountId: invoice.transactionAccountId,
            refundType: CreditNotetype.TAX_ONLY_REFUND,
            paymentMethod: invoice.paymentMethod,
            invoiceCreated: invoice.createdTimestamp,
            refetch: onComplete,
          },
        });
      },
    },
    {
      id: 'ccp-line-items-refund',
      content: 'Refund line items',
      predicate:
        invoice.sourceSystem === SourceSystem.CCP &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isAccess(Permissions.INVOICE_CREDIT_MEMO_WRITE) &&
        !isUncollectible(invoice),
      isDisabled: !isInvoiceRefundable(invoice, hasPaymentTerms),
      action: () => {
        analytics.sendUIEvent(createOpenSidebarFromButtonEvent(GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM_CCP));

        openSidebar({
          sidebarType: GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM_CCP,
          sidebarOptions: {
            sidebarWidth: '600px',
            isOverlay: true,
          },
          payload: {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            transactionAccountId: invoice.transactionAccountId,
            refundType: CreditNotetype.FULL_ITEM_REFUND,
            isRefundLineItems: true,
            lineItems: invoice.lineItems,
            paymentMethod: invoice.paymentMethod,
            invoiceCreated: invoice.createdTimestamp,
            coupledLineItems: invoice.coupledLineItems,
            refetch: onComplete,
          },
        });
      },
    },
    {
      id: 'update-memo',
      content: 'Resend invoice (with optional memo)',
      predicate:
        invoice.sourceSystem === SourceSystem.CCP &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isAccess(Permissions.INVOICE_MANAGE),
      isDisabled: false,
      action: () => {
        openSidebar({
          sidebarType: 'update-memo-pane',
          sidebarOptions: {
            sidebarWidth: '600px',
            isOverlay: true,
          },
          payload: {
            billingDoc: invoice,
            refetch: onComplete,
          },
        });
      },
    },
    {
      id: 'self-serve-bac-invoice-link',
      content: 'Self-serve invoice link',
      predicate:
        invoice.sourceSystem === SourceSystem.CCP &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isAccess(Permissions.INVOICE_CREDIT_MEMO_WRITE),
      isDisabled: !invoice.pdfLink,
      action: () => {
        analytics.sendUIEvent(createOpenSidebarFromButtonEvent(GloballyAllowedSidebar.SELF_SERVE_BAC_INVOICE_LINK));
        openSidebar({
          sidebarType: GloballyAllowedSidebar.SELF_SERVE_BAC_INVOICE_LINK,
          sidebarOptions: {
            sidebarWidth: '600px',
            isOverlay: true,
          },
          payload: {
            selfServeBACInvoiceLink: invoice.selfServeBACInvoiceLink,
            transactionAccountId: invoice.transactionAccountId,
            isPartnerAccount: !!invoice.partnerAccountId,
            invoiceId: invoice.id,
          },
        });
      },
    },
    {
      id: 'cancel-invoice',
      content: 'Void full invoice',
      predicate:
        invoice.sourceSystem === SourceSystem.CCP &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isAccess(Permissions.INVOICE_MANAGE),
      isDisabled: !isUncollectible(invoice),
      action: () => {
        openSidebar({
          sidebarType: 'cancel-invoice',
          sidebarOptions: {
            sidebarWidth: '600px',
            isOverlay: true,
          },
          payload: {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            transactionAccountId: invoice.transactionAccountId,
            refundType: CreditNotetype.DUNNING_CANCELLATION_CREDIT,
            isCancelLineItems: false,
            lineItems: invoice.lineItems,
            paymentMethod: invoice.paymentMethod,
            invoiceStatus: invoice.status,
            invoiceCreated: invoice.createdTimestamp,
            refetch: onComplete,
          },
        });
      },
    },
    {
      id: 'cancel-invoice',
      content: 'Void line items',
      predicate:
        invoice.sourceSystem === SourceSystem.CCP &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isAccess(Permissions.INVOICE_MANAGE),
      isDisabled: !isUncollectible(invoice),
      action: () => {
        analytics.sendUIEvent(createOpenSidebarFromButtonEvent(GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM_CCP));
        openSidebar({
          sidebarType: 'cancel-invoice',
          sidebarOptions: {
            sidebarWidth: '600px',
            isOverlay: true,
          },
          payload: {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            transactionAccountId: invoice.transactionAccountId,
            refundType: CreditNotetype.DUNNING_CANCELLATION_CREDIT,
            isCancelLineItems: true,
            lineItems: invoice.lineItems,
            paymentMethod: invoice.paymentMethod,
            invoiceStatus: invoice.status,
            invoiceCreated: invoice.createdTimestamp,
            refetch: onComplete,
          },
        });
      },
    },
  ];

  return !isSidebarOpen ? <MoreActionsV2 actions={invoiceActions} /> : null;
};

const getInvoiceActions = (billingDoc: BillingDoc, refetch: () => Promise<ApolloQueryResult<unknown>>) => {
  const invoice = billingDoc as InvoiceWithLineItems;

  return (
    <>
      {invoice.sourceSystem === SourceSystem.HAMS && (
        <>
          {/*<AdaRedirectButton href={getViewInAdaInvoiceUrl(billingDoc.id)} />*/}
          <OpenSidebarButton sidebar={'pdf-link-pane'} label="Get PDF link">
            Get PDF link
          </OpenSidebarButton>
        </>
      )}
      <div className="invoice-actions">
        <Button
          iconBefore={<ArrowLeftCircleIcon label="left-arrow" />}
          appearance="subtle"
          onClick={() => history.back()}
          className="invoice-back-button"
        >
          Back
        </Button>
        <InvoiceMoreActions invoice={invoice} onComplete={refetch} />
      </div>
    </>
  );
};

export function hasDueDatePassed(dueDate: string | null) {
  if (!dueDate) {
    return false;
  }

  const currentDate = new Date();
  const inputDate = new Date(dueDate);

  return inputDate < currentDate;
}

const getPaidLabel = (billingDoc: Invoice) => {
  if (
    billingDoc.paymentMethod &&
    // leaving as array in case more items needed to be added
    (['PAIDCC', 'PAIDPAYPAL', 'STRIPE_CARD', 'NOPAYMENT'] as PaymentMethods[]).includes(billingDoc.paymentMethod)
  ) {
    if (billingDoc.status && [InvoicePaymentMethod.PAID].includes(billingDoc.status as InvoicePaymentMethod)) {
      return 'Paid';
    }
    return 'Due by';
  } else if (
    billingDoc.paymentMethod &&
    // leaving as array in case more items needed to be added
    isPaymentTerm(billingDoc.paymentMethod)
  ) {
    if (billingDoc.status && [InvoicePaymentMethod.PAID].includes(billingDoc.status as InvoicePaymentMethod)) {
      return 'Paid';
    }
    return hasDueDatePassed(billingDoc.dueDate) ? (
      <div className={'due-by-label'}>
        <div>Due by</div> <ErrorIcon label="" testId="due-icon" />
      </div>
    ) : (
      'Due by'
    );
  }

  return 'Due by';
};

const getInvoiceAdditionalDetails = (billingDoc?: BillingDocWithLineItems) => {
  if (!billingDoc) {
    return null;
  }

  const invoice = billingDoc as Invoice;
  return (
    <Fragment>
      <SummarySectionV2.SummaryGridCalloutItem styles={{ gridRow: '1' }}>
        <div className="summary-item">
          <h4 className="summary-item-title">Issued</h4>
          <FormattedDate date={invoice.createdTimestamp} showTime={true} format={NEW_DATE_TIME_FORMAT} />
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>

      <SummarySectionV2.SummaryGridCalloutItem styles={{ gridRow: '1' }}>
        <div className="summary-item">
          <h4 className="summary-item-title">{getPaidLabel(billingDoc as Invoice)} </h4>
          <FormattedDate date={invoice.dueDate} format={NEW_DATE_FORMAT} />
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>

      <SummarySectionV2.SummaryGridCalloutItem styles={{ gridRow: '1' }}>
        {/*<FeatureSwitch flag={ENABLE_PO_NUMBER_INVOICE_GROUP}>
            <Enabled> */}
        {invoice.sourceSystem === SourceSystem.CCP && (
          <div className="summary-item">
            <h4 className="summary-item-title">PO number</h4>
            {!isNil(invoice.purchaseOrderNumber) ? invoice?.purchaseOrderNumber : 'Not added'}
          </div>
        )}
      </SummarySectionV2.SummaryGridCalloutItem>
      {/*</div></Enabled>
        </FeatureSwitch> */}
    </Fragment>
  );
};

/*
 * For Atlassian Cloud, find all linked line items and nest underneath
 * For Atlassian Org/AA, leave Identity Manager and filter out Atlassian Org line item
 * For Stride, nest under Atlassian Cloud if there is only one cloud instance on invoice; otherwise make it standalone
 * Otherwise, standalone line item
 */
const getCloudProductLineItemsByCloudUrl = (lineItems: InvoiceLineItem[]): { [key: string]: InvoiceLineItem[] } => {
  const cloudContainerLineItems = lineItems.filter((item) => item.productKey && isCloudParent(item.productKey));

  const cloudSiteHostnames = cloudContainerLineItems
    .map((item) => item.cloudSiteHostname)
    .filter(negate(isNil)) as string[];

  if (cloudSiteHostnames.length === 0) {
    return {};
  }

  const cloudContainerProducts = lineItems.filter(
    (item) =>
      item.cloudSiteHostname &&
      !cloudContainerLineItems.includes(item) &&
      cloudSiteHostnames.includes(item.cloudSiteHostname)
  );

  const strideLineItems = lineItems.filter((item) => item.productKey && isStride(item.productKey));

  if (cloudSiteHostnames.length === 1) {
    return {
      [cloudSiteHostnames[0]]: [...cloudContainerProducts, ...strideLineItems],
    };
  }

  return groupBy(cloudContainerProducts, (product) => product.cloudSiteHostname);
};

/**
 * Separating if needed; customer invoice does not map child / parent relationships
 */
const formatInvoiceLineItemsCCP = (invoiceLineItems: InvoiceLineItem[]) => {
  return invoiceLineItems;
};

const formatInvoiceLineItems = (invoiceLineItems: InvoiceLineItem[]) => {
  const lineItems = invoiceLineItems.filter((item) => item.productKey && !isOrganisationParent(item.productKey));

  const cloudContainerProductMap = getCloudProductLineItemsByCloudUrl(lineItems);

  const cloudContainerProductLineItems = flatten(Object.values(cloudContainerProductMap));

  const topLevelLineItems = lineItems.filter((item) => !cloudContainerProductLineItems.includes(item));

  return topLevelLineItems.map((item) => {
    if (!item.cloudSiteHostname || !cloudContainerProductMap[item.cloudSiteHostname]) {
      return {
        ...item,
        products: [],
      };
    }

    return {
      ...item,
      products: cloudContainerProductMap[item.cloudSiteHostname],
    };
  });
};

const getInvoiceBreadcrumbTrail = (billingDoc: BillingDoc, organization?: Organization) => {
  const { id, invoiceNumber, domain, sourceSystem } = billingDoc as Invoice;
  const breadcrumbTrail: BreadcrumbItem.BreadcrumbItem[] = [];

  // if CCP, prefer Invoice Number (human readable), else fall back to id
  const identifier = sourceSystem === SourceSystem.CCP ? invoiceNumber : id;

  breadcrumbTrail.unshift({
    title: identifier ?? id,
    href: `${INVOICE_PATH_2}/${encodeURIComponent(identifier ?? id)}`,
  });

  if (domain && isValidBreadCrumb(domain)) {
    breadcrumbTrail.unshift({
      title: domain,
      href: `${DOMAIN_PATH}/${encodeURIComponent(domain)}`,
    });
  }

  if (organization && isValidBreadCrumb(organization?.organizationName)) {
    breadcrumbTrail.unshift({
      title: `${organization.organizationName}`,
      href: `${ORGANIZATION_PATH}/${encodeURIComponent(`${organization.organizationId}`)}`,
    });
  }

  return breadcrumbTrail;
};

const isValidBreadCrumb = (token: string | undefined) => {
  return token && !isEmpty(token) && token !== 'undefined';
};

const PdfRedirectButton = ({ href, transactionAccountId, invoiceId, children, sourceSystem }: PdfRedirectProps) => {
  const [pdfLink, setPdfLink] = React.useState(href);
  const [isLoading, setIsLoading] = React.useState(true);
  React.useEffect(() => {
    getCCPInvoicePdfUrl();
  }, []);
  const getCCPInvoicePdfUrl = async () => {
    if (invoiceId && transactionAccountId) {
      const pdfFileUrl = await downloadInvoicePdf(invoiceId, transactionAccountId, sourceSystem);
      const fileUrl = pdfFileUrl ?? href;

      setPdfLink(fileUrl);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <a target="_blank" data-testid="pdf-link-btn" href={pdfLink}>
      {children}
    </a>
  );
};

const getDstLineItemDetails = (lineItems: InvoiceLineItem[] | null | undefined) => {
  let subtotal = 0;
  let taxAmountPaymentCurrency = 0;
  const dstTaxItems: TaxItems[] = [];
  lineItems?.forEach((lineItem: InvoiceLineItem) => {
    subtotal += Number(lineItem.subtotal);
    taxAmountPaymentCurrency += Number(lineItem.taxAmountPaymentCurrency);
    (lineItem?.taxItems || []).forEach((taxItem) => {
      const tax = toNumber(taxItem.tax);
      if (tax && !isNaN(tax)) {
        dstTaxItems.push(taxItem);
      }
    });
  });
  return { subtotal, taxAmountPaymentCurrency, dstTaxItems };
};

const getGroupSortingBy = (a: any, b: any, sortByType: string, similarSortBy?: string) => {
  const labelA = defaultTo(a?.[sortByType], '');
  const labelB = defaultTo(b?.[sortByType], '');
  if (labelA < labelB) {
    return -1;
  }
  if (labelA > labelB) {
    return 1;
  }
  if (labelA === labelB && similarSortBy) {
    // ccp sorting same product names with entitlementNumber in pdf if no cloudSiteHostname exist
    return defaultTo(a?.[similarSortBy], '') < defaultTo(b?.[similarSortBy], '') ? -1 : 1;
  }
  return 0;
};

const getSortedLineItem = (lineItem: InvoiceLineItem[] | null | undefined, sortBy: keyof InvoiceLineItem) => {
  return lineItem?.sort((a, b) => getGroupSortingBy(a, b, sortBy, 'entitlementNumber'));
};

const getFamilyContainerId = (
  entitlement?: InvoiceEssotEntitlementItem,
  relationshipType?: RelationshipPathType | string
) => {
  return entitlement?.relatesFromEntitlements?.find((entitlement) => entitlement?.relationshipType === relationshipType)
    ?.entitlementId;
};

const isCreditInvoiceLineItem = (item: InvoiceLineItem) => {
  if (!item) return false;
  return toNumber(item?.subtotal) < 0 || item?.originalInvoiceReferral?.invoiceNumber;
};

const getGroupNameForApps = (
  coupledLineItems: ProductAppLineItemData,
  groupKey: string,
  selectedProductApps?: InvoiceLineItem[]
) => {
  const defaultAppGroupName = '';
  const coupledApps = defaultTo(
    (coupledLineItems?.[groupKey as keyof ProductAppLineItemData] as ProductAppLineItem)?.apps,
    []
  );
  if (coupledApps && coupledApps.length) {
    const selectedAppId = defaultTo(coupledApps?.[0]?.id, '');
    const isFamilyhasSelectedApp = selectedAppId
      ? selectedProductApps?.find((app) => app?.id === selectedAppId)
      : false;
    return isFamilyhasSelectedApp ? groupKey : defaultAppGroupName;
  }
  return defaultAppGroupName;
};

const getAppParentIdsNotInLineItems = (lineItems: InvoiceLineItem[], appParentIds: string[]) => {
  if (!appParentIds || !lineItems) return [];
  const appParentIdsNotInLineItems = appParentIds.filter(
    (appParentId) =>
      !lineItems.find((lineItem) => {
        const lineItemFamilyContainerId = defaultTo(
          getFamilyContainerId(lineItem?.essotEntitlement, RelationshipPathType.FAMILY_CONTAINER),
          ''
        );
        return lineItemFamilyContainerId === appParentId || lineItem?.entitlementId === appParentId;
      })
  );
  return appParentIdsNotInLineItems;
};

const getSortedInvoiceLineItems = (coupledLineItems: ProductAppLineItemData) => {
  const productsWithFamilyContainer: ProductsWithFamilyContainer = {};
  const productsWithLinkedApps: ProductWithLinkedApps = {};
  const productsWithLinkedAddons: ProductWithLinkedApps = {};
  // we are considering only coupledLineItems which is of type ProductAppLineItem array and we are excluding nullables values
  const coupledLineItemList = Object.values(coupledLineItems)?.filter((coupledLineItem) =>
    Boolean(coupledLineItem)
  ) as ProductAppLineItem[];
  const productLineItemsWithCredits = coupledLineItemList?.flatMap(
    (lineItem: ProductAppLineItem) => lineItem?.products
  );
  const productLineItems = (productLineItemsWithCredits as InvoiceLineItem[])?.filter(
    (lineItem) => !isCreditInvoiceLineItem(lineItem)
  );
  const appLineItems = coupledLineItemList?.flatMap((lineItem: ProductAppLineItem) => lineItem?.apps);
  const addOnsLineItems = coupledLineItemList?.flatMap((lineItem: ProductAppLineItem) => lineItem?.addOns);
  const creditLineItems =
    (productLineItemsWithCredits as InvoiceLineItem[])?.filter((lineItem) => isCreditInvoiceLineItem(lineItem)) ?? [];

  // group products with linked apps
  appLineItems?.forEach((app) => {
    if (app?.essotEntitlement) {
      const familyContainerId = defaultTo(
        getFamilyContainerId(app?.essotEntitlement, RelationshipPathType.APP_DEPENDENCE),
        ''
      );
      if (familyContainerId) {
        if (!productsWithLinkedApps[familyContainerId]) {
          productsWithLinkedApps[familyContainerId] = [];
        }
        productsWithLinkedApps[familyContainerId].push(app as InvoiceLineItem);
      }
    }
  });

  // Check if any app exist without parent
  const appParentIdsNotInLineItems = getAppParentIdsNotInLineItems(
    productLineItemsWithCredits as InvoiceLineItem[],
    Object.keys(productsWithLinkedApps)
  );

  addOnsLineItems?.forEach((addon) => {
    if (addon?.essotEntitlement) {
      const parentId = defaultTo(getFamilyContainerId(addon?.essotEntitlement, RelationshipType.ADDON_DEPENDENCE), '');
      if (parentId) {
        if (!productsWithLinkedAddons[parentId]) {
          productsWithLinkedAddons[parentId] = [];
        }
        productsWithLinkedAddons[parentId].push(addon as InvoiceLineItem);
      }
    }
  });

  // first level: Sort by product names
  const sortedProducts = defaultTo(
    sortBy(productLineItems as InvoiceLineItem[], ['productName', 'entitlementNumber', 'startDate']),
    []
  )?.filter((product) => Boolean(product));

  // group products with same family container or product names
  sortedProducts.forEach((product) => {
    const familyContainerId = defaultTo(
      getFamilyContainerId(product?.essotEntitlement, RelationshipPathType.FAMILY_CONTAINER),
      null
    );
    const productId = product?.productKey ?? product?.entitlement?.productId;
    const groupProductId = familyContainerId ?? productId;
    // group products with same 1. family container or 2. product names
    if (!productsWithFamilyContainer?.[groupProductId]) {
      productsWithFamilyContainer[groupProductId] = {
        familyContainerId: '',
        defaultFamilyName: '',
        defaultCloudSite: '',
        productsItemDetails: [],
        products: [],
      };
    }
    productsWithFamilyContainer?.[groupProductId]?.products.push(product);
    const productsItemDetails = productsWithFamilyContainer[groupProductId]?.productsItemDetails ?? [];
    const isProductAvailableInGroup =
      product?.productName &&
      productsItemDetails?.find((productItem) => productItem?.productName === product?.productName);
    // listing product with same product name to sort further with cloud site
    if (!isProductAvailableInGroup) {
      productsWithFamilyContainer?.[groupProductId]?.productsItemDetails?.push({
        productName: defaultTo(product?.productName, ''),
        cloudSiteHostname: defaultTo(product?.cloudSiteHostname, ''),
      });
    }
  });

  Object.keys(productsWithFamilyContainer).forEach((key: string) => {
    const groupFamilyItems = productsWithFamilyContainer[key];
    if (!groupFamilyItems) return;
    const productsFamiliesData = defaultTo(groupFamilyItems?.productsItemDetails, []);
    // sort the list of names internally to be used for group sorting for family products
    productsFamiliesData?.sort((a, b) => getGroupSortingBy(a, b, 'productName'));

    // will be used for main sorting of products with product names
    groupFamilyItems.defaultFamilyName = defaultTo(productsFamiliesData?.[0]?.productName, '');
    groupFamilyItems.defaultCloudSite = defaultTo(productsFamiliesData?.[0]?.cloudSiteHostname, '');
    groupFamilyItems.familyContainerId = key;

    const sortByType = productsFamiliesData?.length > 1 ? 'productName' : 'cloudSiteHostname';
    groupFamilyItems.products =
      defaultTo(getSortedLineItem(groupFamilyItems.products as InvoiceLineItem[], sortByType), []).filter((product) =>
        Boolean(product)
      ) ?? [];

    // setting up creditItems object to add up any credit items if available at line item level for each product
    groupFamilyItems.creditItems = {};
    groupFamilyItems.appsGroup = {};
    groupFamilyItems.addOnsGroup = {};
    // if (groupFamilyItems.products?.length > 0 && creditLineItems)
    groupFamilyItems.products?.forEach((product) => {
      const appGroupId = productsWithLinkedApps?.[key] ? key : defaultTo(product?.entitlement?.id, '');
      const addOnsGroupId = productsWithLinkedAddons?.[key] ? key : defaultTo(product?.entitlement?.id, '');
      // setting up default appsgroup with empty apps array for the products
      if (appGroupId && groupFamilyItems?.appsGroup && !groupFamilyItems?.appsGroup?.[appGroupId]) {
        groupFamilyItems.appsGroup = {
          [appGroupId]: {
            apps: [],
          },
        };
      }

      if (addOnsGroupId && groupFamilyItems?.addOnsGroup && !groupFamilyItems?.addOnsGroup?.[addOnsGroupId]) {
        groupFamilyItems.addOnsGroup = {
          [addOnsGroupId]: {
            addOns: [],
          },
        };
      }
      // push the apps for selected group / product apps
      if (appGroupId && productsWithLinkedApps?.[appGroupId] && groupFamilyItems.appsGroup) {
        groupFamilyItems.appsGroup[appGroupId].apps = defaultTo(
          getSortedLineItem(productsWithLinkedApps?.[appGroupId], 'productName'),
          []
        );
        // for apps row heading finding the family container name Jira | Confluence
        Object.keys(coupledLineItems)?.forEach((key) => {
          const groupName = getGroupNameForApps(coupledLineItems, key, groupFamilyItems?.appsGroup?.[appGroupId].apps);
          if (groupName && groupFamilyItems?.appsGroup?.[appGroupId]) {
            groupFamilyItems.appsGroup[appGroupId].groupName = groupName;
          }
        });
      }

      if (addOnsGroupId && productsWithLinkedAddons?.[addOnsGroupId] && groupFamilyItems.addOnsGroup) {
        groupFamilyItems.addOnsGroup[addOnsGroupId].addOns = defaultTo(
          getSortedLineItem(productsWithLinkedAddons?.[addOnsGroupId], 'productName'),
          []
        );
      }

      if (product?.entitlementNumber) {
        /* if creditLineItems list has credit for selected product entitlement
         * but groupFamilyItems.creditItems is still not set with the credit than add the credit to groupFamilyItems.creditItems at product level
         */
        const creditListForSelectedProducts =
          creditLineItems?.filter((credit) => credit.entitlementNumber === product.entitlementNumber) ?? [];

        const creditItemForSelectedProducts = groupFamilyItems.creditItems?.[product.entitlementNumber];
        if (creditListForSelectedProducts.length > 0 && !creditItemForSelectedProducts) {
          // @ts-ignore
          set(groupFamilyItems.creditItems, product.entitlementNumber, {
            [product.invoiceLineItemId as string]: creditListForSelectedProducts,
          });
        }
      }
    });
  });

  // missing case where only app line items found in invoice without any product
  if (
    (sortedProducts?.length === 0 || Object.keys(productsWithFamilyContainer)?.length === 0) &&
    Object.keys(productsWithLinkedApps)?.length > 0
  ) {
    Object.keys(productsWithLinkedApps)?.forEach((linkedProductKey) => {
      if (productsWithLinkedApps?.[linkedProductKey]) {
        let groupName = '';
        // for apps row heading finding the family container name Jira | Confluence
        Object.keys(coupledLineItems)?.forEach((key) => {
          const groupHeadingName = getGroupNameForApps(
            coupledLineItems,
            key,
            productsWithLinkedApps?.[linkedProductKey]
          );
          if (groupHeadingName) groupName = groupHeadingName;
        });
        productsWithFamilyContainer[linkedProductKey] = {
          defaultCloudSite: defaultTo(productsWithLinkedApps?.[linkedProductKey]?.[0]?.cloudSiteHostname, ''),
          familyContainerId: linkedProductKey,
          defaultFamilyName: defaultTo(productsWithLinkedApps?.[linkedProductKey]?.[0]?.productName, ''),
          products: [],
          appsGroup: {
            [linkedProductKey]: {
              apps: defaultTo(getSortedLineItem(productsWithLinkedApps?.[linkedProductKey], 'productName'), []),
              groupName: groupName,
            },
          },
        };
      }
    });
  }

  // missing case where only app line items found in invoice without any product but having other line items
  /**
   * appParentIdsNotInLineItems length non zero can be for two reasons:
   * 1. when only app line items found in invoice without any product
   * 2. when app line items found in invoice without any product but having other line items
   *
   * case 1 is already handled above
   * In case 2, we need to check if the app line items are already added to the productsWithFamilyContainer
   * If not, then we need to add them to the productsWithFamilyContainer
   */
  if (
    appParentIdsNotInLineItems?.length !== 0 &&
    (sortedProducts?.length !== 0 || Object.keys(productsWithFamilyContainer)?.length !== 0)
  ) {
    appParentIdsNotInLineItems?.forEach((appParentId) => {
      if (productsWithLinkedApps?.[appParentId]) {
        let groupName = '';
        // for apps row heading finding the family container name Jira | Confluence
        Object.keys(coupledLineItems)?.forEach((key) => {
          const groupHeadingName = getGroupNameForApps(coupledLineItems, key, productsWithLinkedApps?.[appParentId]);
          if (groupHeadingName) groupName = groupHeadingName;
        });
        productsWithFamilyContainer[appParentId] = {
          defaultCloudSite: defaultTo(productsWithLinkedApps?.[appParentId]?.[0]?.cloudSiteHostname, ''),
          familyContainerId: appParentId,
          defaultFamilyName: defaultTo(productsWithLinkedApps?.[appParentId]?.[0]?.productName, ''),
          products: [],
          appsGroup: {
            [appParentId]: {
              apps: defaultTo(getSortedLineItem(productsWithLinkedApps?.[appParentId], 'productName'), []),
              groupName: groupName,
            },
          },
        };
      }
    });
  }

  // where only add on line items are found in invoice without any parent product
  if (
    (sortedProducts?.length === 0 || Object.keys(productsWithFamilyContainer)?.length === 0) &&
    Object.keys(productsWithLinkedAddons)?.length > 0
  ) {
    Object.keys(productsWithLinkedAddons)?.forEach((linkedProductKey) => {
      if (productsWithLinkedAddons?.[linkedProductKey]) {
        productsWithFamilyContainer[linkedProductKey] = {
          defaultCloudSite: defaultTo(productsWithLinkedAddons?.[linkedProductKey]?.[0]?.cloudSiteHostname, ''),
          familyContainerId: linkedProductKey,
          defaultFamilyName: defaultTo(productsWithLinkedAddons?.[linkedProductKey]?.[0]?.productName, ''),
          products: [],
          addOnsGroup: {
            [linkedProductKey]: {
              addOns: defaultTo(getSortedLineItem(productsWithLinkedAddons?.[linkedProductKey], 'productName'), []),
              groupName: 'Add-ons',
            },
          },
        };
      }
    });
  }
  // final product sorting
  const sortedCoupledLineItems = Object.values(productsWithFamilyContainer)?.sort((a, b) =>
    getGroupSortingBy(a, b, 'defaultFamilyName', 'defaultCloudSite')
  );
  return sortedCoupledLineItems;
};

const isApp = (
  relatesFromEntitlements: generatedTypesNew.Maybe<generatedTypesNew.RelatedEntitlementDetails>[] | undefined | null
) => {
  return !!relatesFromEntitlements?.some(
    (entitlementRelation) => (entitlementRelation?.relationshipType as unknown) === RelationshipPathType.APP_DEPENDENCE
  );
};

const isAddOn = (
  relatesFromEntitlements: generatedTypesNew.Maybe<generatedTypesNew.RelatedEntitlementDetails>[] | undefined | null
) => {
  return !!relatesFromEntitlements?.some(
    (entitlementRelation) => (entitlementRelation?.relationshipType as unknown) === RelationshipType.ADDON_DEPENDENCE
  );
};

const getEntIdFromRelatesFrom = (
  entitlement: InvoiceEntitlementItem | null | undefined,
  relationshipType?: RelationshipPathType | string
) => {
  return entitlement?.relatesFromEntitlements?.find((entitlement) => entitlement?.relationshipType === relationshipType)
    ?.entitlementId;
};

const getProcessedLineItems = (lineItems: InvoiceLineItems[]) => {
  // Store each line items in processedLineItems object as need to show in UI
  const processedLineItems: {
    [key: string]: {
      products: InvoiceLineItems[];
      instances: InvoiceLineItems[];
      apps: InvoiceLineItems[];
      addOns: InvoiceLineItems[];
    };
  } = {};

  // sort the line items based on product name
  lineItems.sort((a, b) => getGroupSortingBy(a, b, 'productName'));

  // group all the line items based on category it falls into
  const collections: InvoiceLineItems[] = [];
  const collectionsInstances: InvoiceLineItems[] = [];
  const products: InvoiceLineItems[] = [];
  const apps: InvoiceLineItems[] = [];
  const addOns: InvoiceLineItems[] = [];
  lineItems.forEach((lineItem) => {
    if (isCollectionProductEntitlement(lineItem.entitlement?.productId)) {
      collections.push(lineItem);
    } else if (isCollectionInstanceEntitlement(lineItem.entitlement?.relatesFromEntitlements)) {
      collectionsInstances.push(lineItem);
    } else if (isApp(lineItem.entitlement?.relatesFromEntitlements)) {
      apps.push(lineItem);
    } else if (isAddOn(lineItem.entitlement?.relatesFromEntitlements)) {
      addOns.push(lineItem);
    } else {
      products.push(lineItem);
    }
  });

  // store the grouped line items in processedLineItems object
  collections.forEach((collection) => {
    const filteredCollectionInstances = collectionsInstances.filter(
      (collectionInstance) =>
        getEntIdFromRelatesFrom(collectionInstance.entitlement, SUBSCRIPTION_CONSTANTS.COLLECTION_ENTITLEMENT_TYPE) ===
        collection.id
    );
    if (collection.id) {
      processedLineItems[collection.id] = {
        products: [collection],
        instances: filteredCollectionInstances,
        apps: [],
        addOns: [],
      };
    }
  });

  collectionsInstances.forEach((collectionInstance) => {
    const collectionId = getEntIdFromRelatesFrom(
      collectionInstance.entitlement,
      SUBSCRIPTION_CONSTANTS.COLLECTION_ENTITLEMENT_TYPE
    );

    // if the collection instance is not mapped to any collection, then add it as standalone product
    if (collectionId && !processedLineItems[collectionId]) {
      processedLineItems[collectionId] = {
        products: [collectionInstance],
        instances: [],
        apps: [],
        addOns: [],
      };
    }
  });

  products.forEach((product) => {
    // Check if the product is a part of family container
    const familyContainerId = product.entitlement?.relatesFromEntitlements?.find(
      (entitlement) => entitlement?.relationshipType === RelationshipPathType.FAMILY_CONTAINER
    )?.entitlementId;

    if (familyContainerId) {
      if (processedLineItems[familyContainerId]) {
        processedLineItems[familyContainerId].products.push(product);
      } else {
        processedLineItems[familyContainerId] = {
          products: [product],
          instances: [],
          apps: [],
          addOns: [],
        };
      }
    }

    if (product.id && !familyContainerId) {
      processedLineItems[product.id] = {
        products: [product],
        instances: [],
        apps: [],
        addOns: [],
      };
    }
  });

  addOns.forEach((addOn) => {
    const parentId = getEntIdFromRelatesFrom(addOn.entitlement, RelationshipType.ADDON_DEPENDENCE);

    if (parentId && processedLineItems[parentId]) {
      processedLineItems[parentId].addOns.push(addOn);
    }
    // Add ons won't exist without parent product
  });

  apps.forEach((app) => {
    /**
     * Map each app to its parent product
     * Parent product is decided based on below logic:
     * 1. If the app has a family container, then product with maximum quantity is considered as parent product
     * 2. If the product of family with maximum quantity is of collection instance, then map with collection parent entitlement id
     * 2. If the app does not have a family container, then map with parent entitlement id
     */

    // find all family conatiner IDs from the lineItems
    const allfamilyContainerIds = lineItems
      .map(
        (lineItem) =>
          lineItem.entitlement?.relatesFromEntitlements?.find(
            (entitlement) => entitlement?.relationshipType === RelationshipPathType.FAMILY_CONTAINER
          )?.entitlementId
      )
      .filter(Boolean);

    const parentProductOrContainerEntId = app.entitlement?.relatesFromEntitlements?.find(
      (entitlement) => entitlement?.relationshipType === RelationshipPathType.APP_DEPENDENCE
    )?.entitlementId;

    const isAppPartOfFamilyContainer = allfamilyContainerIds.includes(parentProductOrContainerEntId);

    if (isAppPartOfFamilyContainer) {
      const familyProducts = lineItems.filter(
        (lineItem) =>
          lineItem.entitlement?.relatesFromEntitlements?.find(
            (entitlement) => entitlement?.relationshipType === RelationshipPathType.FAMILY_CONTAINER
          )?.entitlementId === parentProductOrContainerEntId
      );
      const familyProductWithMaxQuantity = familyProducts.reduce((prev, current) =>
        (prev.quantity ?? 0) > (current.quantity ?? 0) ? prev : current
      );

      const isFamilyProductCollectionInstance = familyProductWithMaxQuantity?.entitlement?.relatesFromEntitlements?.some(
        (entitlement) =>
          (entitlement?.relationshipType as unknown) === SUBSCRIPTION_CONSTANTS.COLLECTION_ENTITLEMENT_TYPE
      );

      // if the family product is collection instance, then map with collection parent entitlement id
      if (isFamilyProductCollectionInstance) {
        const familyProductWithMaxQuantityCollectionId = getEntIdFromRelatesFrom(
          familyProductWithMaxQuantity?.entitlement,
          SUBSCRIPTION_CONSTANTS.COLLECTION_ENTITLEMENT_TYPE
        );

        if (familyProductWithMaxQuantityCollectionId && processedLineItems[familyProductWithMaxQuantityCollectionId]) {
          processedLineItems[familyProductWithMaxQuantityCollectionId].apps.push(app);
        }
      } else {
        if (parentProductOrContainerEntId && processedLineItems[parentProductOrContainerEntId]) {
          processedLineItems[parentProductOrContainerEntId].apps.push(app);
        }
      }
    } else if (parentProductOrContainerEntId && processedLineItems[parentProductOrContainerEntId]) {
      // if the app is not a part of family container, then map it with parent product
      processedLineItems[parentProductOrContainerEntId].apps.push(app);
    } else if (parentProductOrContainerEntId && !processedLineItems[parentProductOrContainerEntId]) {
      // if the app is not mapped to any product, then add it as standalone app
      processedLineItems[parentProductOrContainerEntId] = {
        products: [],
        instances: [],
        apps: [app],
        addOns: [],
      };
    }
  });

  return processedLineItems;
};

const getPartnerMarginItems = (margins: any) => {
  return margins?.filter((margin: any) => margin?.type === MarginType.PARTNER_MARGIN);
};

export {
  formatInvoiceLineItems,
  formatInvoiceLineItemsCCP,
  getInvoiceActions,
  getInvoiceAdditionalDetails,
  getInvoiceHeader,
  getInvoiceSubheader,
  getInvoiceBreadcrumbTrail,
  PdfRedirectButton,
  getDstLineItemDetails,
  getSortedInvoiceLineItems,
  isCreditInvoiceLineItem,
  getPartnerMarginItems,
  getProcessedLineItems,
};
